<template>
  <div>
    <menu-pages-navbar class="mb-3" />
    <!-- MenuAdditives -->
    <div>
      <v-layout justify-end>
        <!-- Create MenuAdditive Button -->
        <v-slide-x-transition>
          <v-btn
            color="msr"
            dark
            round
            class="mt-3 ml-auto mr-0"
            @click="showCreateMenuAdditiveDialog = true"
          >
            <v-icon left>
              add
            </v-icon>
            {{ $t('menuAdditive') }}
          </v-btn>
        </v-slide-x-transition>
      </v-layout>
      <v-slide-y-transition>
        <v-data-table
          hide-actions
          :headers="menuAdditiveHeaders"
          :items="menuAdditives"
          :loading="loadingAdditives"
          :no-data-text="loadingAdditives ? '' : $t('menuAdditivesNoDataText')"
          class="mt-3"
        >
          <template
            slot="items"
            slot-scope="props"
          >
            <!-- Name -->
            <td :title="$t('edit')">
              <v-edit-dialog
                :return-value.sync="props.item.name"
                lazy
                @save="updateMenuAdditive(props.item)"
              >
                {{ props.item.name }}
                <template slot="input">
                  <v-text-field
                    v-model="props.item.name"
                    class="mt-2"
                    :rules="[
                      ruleFactories.menuAdditiveMustHaveUniqueName(props.item),
                    ]"
                    :label="$t('name')"
                  />
                </template>
              </v-edit-dialog>
            </td>
            <!-- Index -->
            <td :title="$t('edit')">
              <v-edit-dialog
                :return-value.sync="props.item.index"
                lazy
                @save="updateMenuAdditive(props.item)"
              >
                {{ props.item.index }}
                <template slot="input">
                  <v-text-field
                    v-model="props.item.index"
                    type="number"
                    class="mt-2"
                    :rules="[
                      rules.positiveNumber,
                      ruleFactories.menuAdditiveMustHaveUniqueIndex(props.item),
                    ]"
                    :label="$t('numberedIndex')"
                  />
                </template>
              </v-edit-dialog>
            </td>
            <!-- Number of menu items used on -->
            <td class="menuitem-link-wrapper">
              <v-menu
                lazy
                class="full-width"
              >
                <div
                  slot="activator"
                  class="full-width"
                >
                  <v-icon color="grey lighten-1">
                    fastfood
                  </v-icon>
                  <span
                    style="
                      display: inline-block;
                      min-width: 32px;
                      text-align: right;
                    "
                    class="menuitem-link unselectable mx-0 subheading"
                    :class="{
                      'grey--text text--darken-1':
                        props.item.num_menu_items_used_on > 0,
                      'warning--text': props.item.num_menu_items_used_on == 0,
                    }"
                  >
                    <strong>{{ props.item.num_menu_items_used_on }}</strong>
                  </span>
                </div>
                <v-list>
                  <div class="px-3 py-2 caption grey--text text--darken-2">
                    <span v-if="props.item.num_menu_items_used_on == 0">{{
                      $t('menuAdditiveNotUsedYet', {name: props.item.name})
                    }}</span>
                    <span v-else>{{
                      $t('menuAdditiveUsedIn', {name: props.item.name})
                    }}</span>
                  </div>
                  <v-list-tile
                    v-for="menuItem in props.item.menu_items"
                    :key="menuItem.id"
                    class="py-1"
                    :title="$t('goToMenuItem')"
                    @click="
                      $router.push({
                        name: 'editmenuitem',
                        params: {menuItemId: menuItem.id},
                        query: {menu: menuId},
                      })
                    "
                  >
                    <v-list-tile-content>
                      <v-list-tile-title>{{ menuItem.name }}</v-list-tile-title>
                      <v-list-tile-sub-title>
                        {{ menuItem.category.name }}
                      </v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </v-list>
              </v-menu>
            </td>
            <!-- Delete -->
            <td
              style="width: 32px; padding-right: 0"
              :title="
                !!additiveIdIsUsedInMenuItemOrVariants[props.item.id]
                  ? $t('menuAdditiveInUseCannotBeDeleted')
                  : $t('remove')
              "
            >
              <v-btn
                class="my-0 grey--text"
                :style="{
                  opacity: !!additiveIdIsUsedInMenuItemOrVariants[props.item.id]
                    ? 0.33
                    : 1.0,
                }"
                icon
                flat
                :disabled="
                  loadingAdditives ||
                    !!additiveIdIsUsedInMenuItemOrVariants[props.item.id]
                "
                @click="confirmDeleteMenuAdditive(props.item)"
              >
                <v-icon> delete </v-icon>
              </v-btn>
            </td>
          </template>
        </v-data-table>
      </v-slide-y-transition>
    </div>
    <!-- Scroll Spacer -->
    <div style="min-height: 60px" />
    <!-- Create MenuAdditive Dialog -->
    <create-menu-additive-dialog
      :show.sync="showCreateMenuAdditiveDialog"
      :menu-additives="menuAdditives"
      @save="(name) => createMenuAdditive(name)"
    />
  </div>
</template>

<script>
import {mapState} from 'vuex';

import CreateMenuAdditiveDialog from '@/components/Menu/CreateMenuAdditiveDialog';
import MenuPagesNavbar from '@/components/Menu/MenuPagesNavbar';

export default {
  components: {
    CreateMenuAdditiveDialog,
    MenuPagesNavbar,
  },
  data() {
    return {
      loadingMenu: false,
      loadingAdditives: false,

      showCreateMenuAdditiveDialog: false,

      menuAdditiveHeaders: [
        {
          text: this.$t('name'),
          value: 'name',
          sortable: true,
        },
        {
          text: this.$t('numberedIndex'),
          value: 'index',
          sortable: true,
        },
        {
          text: this.$t('menuCategoryNumMenuItemsUsedOn'),
          value: 'num_menu_items_used_on',
          sortable: true,
        },
      ],

      // From backend.
      menu: null,
    };
  },
  computed: {
    ...mapState({
      menuAdditives: (state) => state.common.menuAdditives,
      menuItems: (state) => state.common.menuItems,
    }),
    additiveIdIsUsedInMenuItemOrVariants() {
      const additiveIdToUsed = {};
      for (const menuItem of this.menuItems) {
        for (const additive of menuItem.additives) {
          additiveIdToUsed[additive.id] = true;
        }
        for (const variant of menuItem.variants) {
          for (const additive of variant.additives) {
            additiveIdToUsed[additive.id] = true;
          }
        }
      }
      return additiveIdToUsed;
    },
    ruleFactories() {
      /**
       *  Create rules that need knowledge about which item is being edited.
       */
      return {
        menuAdditiveMustHaveUniqueIndex: (menuAdditive) => {
          const vm = this;
          function rule(index) {
            const otherMenuAdditiveIndices = vm.menuAdditives
              .filter((additive) => additive.id !== menuAdditive.id)
              .map((additive) => Number(additive.index));
            return (
              !otherMenuAdditiveIndices.includes(Number(index)) ||
              vm.$t('alreadyInUse')
            );
          }
          return rule;
        },
        menuAdditiveMustHaveUniqueName: (menuAdditive) => {
          const vm = this;
          function rule(name) {
            const otherMenuAdditiveNames = vm.menuAdditives
              .filter((additive) => additive.id !== menuAdditive.id)
              .map((additive) => additive.name.toLowerCase());
            return (
              !otherMenuAdditiveNames.includes(name.toLowerCase()) ||
              vm.$t('alreadyInUse')
            );
          }
          return rule;
        },
      };
    },
    rules() {
      const vm = this;
      return {
        positiveNumber: (v) =>
          v > 0 || vm.$t('menuItemAdditiveIndexMustBePositive'),
      };
    },
    menuId() {
      return this.$route.params.menuId;
    },
  },
  methods: {
    // MenuAdditive
    createMenuAdditive(name) {
      this.$api.createMenuAdditive(this.menuId, name).then(() => {
        this.showCreateMenuAdditiveDialog = false;
        this.fetchMenuAdditives();
        this.fetchMenuItems();
      });
    },
    updateMenuAdditive(menuAdditive) {
      this.$api
        .updateMenuAdditive(menuAdditive.id, menuAdditive)
        .catch((error) => {
          console.error(error);
          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .finally(() => {
          this.fetchMenuAdditives();
          this.fetchMenuItems();
        });
    },
    confirmDeleteMenuAdditive(menuAdditive) {
      const confirmed = confirm(this.$t('menuAdditiveConfirmDeletion'));
      if (!confirmed) {
        return;
      }
      this.$api.deleteMenuAdditive(menuAdditive.id).then(() => {
        this.fetchMenuAdditives();
        this.fetchMenuItems();
      });
    },
    fetchData() {
      this.fetchMenu({indicator: true});
      this.fetchMenuAdditives({indicator: true});
      if (!this.menuItems.length) {
        this.fetchMenuItems();
      }
    },
    fetchMenu({indicator = false} = {}) {
      if (indicator) {
        this.loadingMenu = true;
      }
      this.$api
        .retrieveMenu(this.menuId)
        .then((response) => {
          this.menu = response.data;
        })
        .finally(() => (this.loadingMenu = false));
    },
    fetchMenuAdditives({indicator = false} = {}) {
      if (indicator) {
        this.loadingAdditives = true;
      }
      this.$store
        .dispatch('LIST_MENU_ADDITIVES', this.menuId)
        .finally(() => (this.loadingAdditives = false));
    },
    fetchMenuItems() {
      return this.$store.dispatch('LIST_MENU_ITEMS', this.menuId);
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.fetchData();
    });
  },
};
</script>

<style scoped>
.menuitem-link-wrapper {
  cursor: pointer;
}
.menuitem-link-wrapper:hover .menuitem-link {
  text-decoration: underline;
  color: var(--msr-color-primary) !important;
}
</style>



// WEBPACK FOOTER //
// src/pages/MenuAdditives.vue